import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Journey } from "../components/onboarding/Journey";
import { realPnlKey, useLiveRealPnl, type RealPnl } from "@/hooks/useLiveRealPnl";
import { useRecentResults } from "@/hooks/useRecentResults";
import { FirstRun } from "../components/onboarding/FirstRun";
import { useApi } from "../context/ApiContext";
import type { Account, Backtest, Position } from "@/api/types";
import { PositionsTable, type PositionRow } from "@/components/PositionsTable";
import { formatMoney } from "@/lib/backtest-stats";
import { cn } from "@/lib/utils";
import { Card, CardHead, Pill, SmallButton, statusPill, ExchangeMark } from "../components/ui/kit";
import { isLabInstance, isLiveInstance, liveInstancesFor } from "@/lib/live";
import { storedSummary } from "@/lib/backtest-summary";
import { useUiStore } from "@/stores/ui";

function Kpi({ label, value, color, hint }: { label: string; value: string; color?: string; hint?: string }) {
  return (
    <Card className="px-4 py-3.5">
      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--text-muted)]">{label}</div>
      <div className="num mt-1.5 text-[22px] font-semibold tracking-[-0.02em]" style={{ color: color || "var(--text-primary)" }}>
        {value}
      </div>
      {hint && <div className="mt-0.5 text-xs text-[var(--text-muted)]">{hint}</div>}
    </Card>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return "Good evening";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function timeAgo(iso?: string): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return "";
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  const d = Math.floor(s / 86400);
  return d === 1 ? "yesterday" : `${d} days ago`;
}

function pnlColor(n: number | null | undefined): string {
  if (n == null) return "var(--text-muted)";
  return n >= 0 ? "var(--trading-profit)" : "var(--trading-loss)";
}

const cell = "px-3.5 py-[11px] text-[13px]";

export default function HomePage() {
  const api = useApi(["accounts", "backtests"]);
  const accounts: Account[] = useMemo(() => api.accounts || [], [api.accounts]);
  const backtests: Backtest[] = useMemo(() => api.backtests || [], [api.backtests]);
  const openNewBacktest = useUiStore((st) => st.openNewBacktest);
  const openGoLive = useUiStore((st) => st.openGoLive);

  const live = useMemo(() => backtests.filter(isLiveInstance), [backtests]);
  const real = useLiveRealPnl(live);
  const recent = useRecentResults(30);
  const lab = useMemo(
    () =>
      backtests
        .filter(isLabInstance)
        .sort((a, b) => Date.parse(b.updated_at || "") - Date.parse(a.updated_at || "")),
    [backtests]
  );

  // Live strategies that are switched on, grouped by the exchange account they trade on.
  // Stopped ones are left out; ones that need a look show under "Needs your attention".
  const liveByAccount = useMemo(() => {
    const active = live.filter((bt) => bt.enabled && bt.status !== "stopped");
    const groups: { account: Account | null; rows: Backtest[] }[] = [];
    for (const acc of accounts) {
      const rows = active.filter((bt) => bt.accounts?.some((a) => a.account_id === acc.id));
      if (rows.length) groups.push({ account: acc, rows });
    }
    const unlinked = active.filter((bt) => !bt.accounts?.some((a) => accounts.some((acc) => acc.id === a.account_id)));
    if (unlinked.length) groups.push({ account: null, rows: unlinked });
    return groups;
  }, [accounts, live]);

  const exchangesInUse = liveByAccount.filter((g) => g.account).length;

  const attention = useMemo(() => {
    const items: { key: string; tone: string; title: string; detail: string; to: string; cta: string }[] = [];
    for (const bt of live) {
      if (bt.enabled && bt.status === "stopped") {
        items.push({
          key: `bt-${bt.id}`,
          tone: "var(--warning)",
          title: `${bt.name} is switched on but not running`,
          detail: bt.status_msg || "It isn't placing orders.",
          to: `/live/${bt.id}`,
          cta: "Review",
        });
      } else if (bt.status === "error") {
        items.push({
          key: `bt-${bt.id}`,
          tone: "var(--danger)",
          title: `${bt.name} stopped with an error`,
          detail: bt.status_msg || "Open it to see what happened.",
          to: `/live/${bt.id}`,
          cta: "Review",
        });
      }
    }
    for (const acc of accounts) {
      const using = live.filter((bt) => bt.enabled && bt.accounts?.some((a) => a.account_id === acc.id)).length;
      const offline = acc.status === "stopped" || acc.status === "disabled" || !acc.enabled;
      if (acc.status !== "error" && !(using && offline)) continue;
      items.push({
        key: `acc-${acc.id}`,
        tone: "var(--danger)",
        title: acc.status === "error" ? `${acc.name} can't connect` : `${acc.name} is disconnected`,
        detail:
          [acc.status_msg, using ? `${using} live ${using === 1 ? "strategy" : "strategies"} affected` : ""]
            .filter(Boolean)
            .join(" · ") || "Live strategies on it can't place orders.",
        to: `/integrations/marketplaces/${acc.id}`,
        cta: "Fix",
      });
    }
    return items;
  }, [accounts, live]);

  // Every open position, across all accounts (also ones opened outside a strategy).
  const positions: PositionRow[] = useMemo(
    () =>
      accounts.flatMap((account) =>
        (api.positionsByAccount?.[account.id] || []).map((p: Position) => {
          // The exchange reports a perpetual without the ".P" the strategy's symbol carries.
          const same = (sym?: string) => String(sym || "").toUpperCase().replace(/\.P$/, "") === p.symbol.toUpperCase().replace(/\.P$/, "");
          const owners = live.filter((bt) => same(bt.symbol) && bt.accounts?.some((a) => a.account_id === account.id));
          return { ...p, account, strategyHref: owners.length === 1 ? `/live/${owners[0]!.id}` : undefined };
        })
      ),
    [accounts, api.positionsByAccount, live]
  );
  const unrealized = positions.reduce((sum, p) => sum + (Number(p.unrealized_pnl) || 0), 0);

  const firstName = api.user?.name?.trim().split(/\s+/)[0];
  const summary = [
    live.length
      ? `${api.liveRunning} live${exchangesInUse ? ` on ${exchangesInUse} ${exchangesInUse === 1 ? "exchange" : "exchanges"}` : ""}`
      : "Nothing live yet",
    `${lab.length} ${lab.length === 1 ? "backtest" : "backtests"}`,
  ].join(" · ");

  // Nothing to report yet: show the first-run screen instead of empty panels.
  if (api.ready && lab.length === 0 && live.length === 0) {
    return (
      <div className="flex flex-col gap-5">
        <Journey accounts={accounts} backtests={backtests} />
        <FirstRun firstName={firstName} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <div className="min-w-0">
          <h1 className="m-0 text-[22px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
            {greeting()}
            {firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="m-0 mt-1 text-[13px] text-[var(--text-muted)]">{summary}</p>
        </div>
        <button
          type="button"
          onClick={openNewBacktest}
          className="btn btn-primary"
        >
          + New backtest
        </button>
      </div>

      <Journey accounts={accounts} backtests={backtests} />

      {accounts.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Kpi
            label="Unrealized P&L"
            value={positions.length ? formatMoney(unrealized) : "—"}
            color={positions.length ? pnlColor(unrealized) : undefined}
            hint={positions.length ? `${positions.length} open ${positions.length === 1 ? "position" : "positions"}` : "no open positions"}
          />
          <Kpi
            label="Realized P&L · 30 days"
            value={recent.loaded ? formatMoney(recent.pnl) : "…"}
            color={recent.closed ? pnlColor(recent.pnl) : undefined}
            hint={`${recent.closed} closed ${recent.closed === 1 ? "trade" : "trades"} on your exchanges`}
          />
          <Kpi
            label="Win rate · live, 30 days"
            value={recent.winRate == null ? "—" : `${Math.round(recent.winRate * 100)}%`}
            hint={recent.closed ? `${recent.wins} of ${recent.closed} trades won` : "no closed trades yet"}
          />
        </div>
      )}

      {attention.length > 0 && (
        <Card>
          <CardHead title="Needs your attention">
            <Pill tone="warn" dot={false}>
              {attention.length}
            </Pill>
          </CardHead>
          {attention.map((item, i) => (
            <div
              key={item.key}
              className={cn("flex items-center gap-3 px-4 py-3", i > 0 && "border-t border-[var(--divider)]")}
            >
              <span className="w-1 self-stretch rounded" style={{ background: item.tone }} />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-[var(--text-primary)]">{item.title}</div>
                {item.detail && <div className="truncate text-xs text-[var(--text-muted)]">{item.detail}</div>}
              </div>
              <SmallButton to={item.to}>{item.cta}</SmallButton>
            </div>
          ))}
        </Card>
      )}

      {accounts.length > 0 && (
        <Card>
          <CardHead title="Open positions" />
          <PositionsTable positions={positions} empty="No open positions on your exchanges." />
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHead title="Live now">
            <SmallButton to="/live" variant="ghost">
              All ›
            </SmallButton>
          </CardHead>
          {liveByAccount.length === 0 ? (
            <p className="m-0 px-4 pb-5 pt-1 text-[13px] text-[var(--text-muted)]">
              Nothing is trading yet.{" "}
              <button type="button" onClick={openGoLive} className="text-[var(--primary)]">
                Go live with a backtest
              </button>
            </p>
          ) : (
            <table className="w-full border-collapse">
              <tbody>
                {liveByAccount.map((g) => (
                  <GroupRows key={g.account?.id ?? "none"} account={g.account} rows={g.rows} real={real} />
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card>
          <CardHead title="Recent backtests">
            <SmallButton to="/lab/backtest" variant="ghost">
              All {lab.length} ›
            </SmallButton>
          </CardHead>
          {lab.length === 0 ? (
            <p className="m-0 px-4 pb-5 pt-1 text-[13px] text-[var(--text-muted)]">
              No backtests yet.{" "}
              <button type="button" onClick={openNewBacktest} className="text-[var(--primary)]">
                Create your first one
              </button>
            </p>
          ) : (
            <table className="w-full border-collapse">
              <tbody>
                {lab.slice(0, 4).map((bt) => {
                  // The last run's numbers, saved on the backtest when it ran (as on the backtests page).
                  const sum = storedSummary(bt.metadata);
                  const isLive = liveInstancesFor(bt, backtests).length > 0;
                  const goLive = !!sum && sum.pnl > 0 && !isLive;
                  return (
                    <tr key={bt.id} className="border-t border-[var(--divider)] hover:bg-[var(--bg-hover)]">
                      <td className={cell}>
                        <Link
                          to={`/lab/backtest/${bt.id}`}
                          className="font-semibold text-[var(--text-primary)] no-underline"
                        >
                          {bt.name}
                        </Link>
                        <div className="text-xs text-[var(--text-muted)]">
                          {[bt.symbol, bt.timeframe, isLive ? "live" : timeAgo(bt.updated_at)].filter(Boolean).join(" · ")}
                        </div>
                      </td>
                      <td className={cn(cell, "num text-right")} style={{ color: sum ? pnlColor(sum.pnl) : "var(--text-muted)" }}>
                        {sum ? formatMoney(sum.pnl) : "—"}
                      </td>
                      <td className={cn(cell, "num text-right")}>
                        {sum && sum.trades > 0 ? (
                          <>
                            {Math.round(sum.win_rate * 100)}%
                            <div className="text-[11px] font-normal text-[var(--text-muted)]">{Math.round(sum.win_rate * sum.trades)}/{sum.trades} won</div>
                          </>
                        ) : (
                          <span className="text-[var(--text-muted)]">{sum ? "No trades" : "—"}</span>
                        )}
                      </td>
                      <td className={cn(cell, "w-px text-right")}>
                        {goLive ? (
                          <button type="button" className="btn btn-sm btn-primary" onClick={() => openGoLive(bt.id)}>
                            Go live
                          </button>
                        ) : (
                          <SmallButton to={`/lab/backtest/${bt.id}`}>Open</SmallButton>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      </div>

    </div>
  );
}

function GroupRows({ account, rows, real }: { account: Account | null; rows: Backtest[]; real: Record<string, RealPnl> }) {
  return (
    <>
      <tr className="border-t border-[var(--divider)]">
        <td colSpan={4} className="px-3.5 pb-1 pt-2.5 text-xs text-[var(--text-muted)]">
          <span className="inline-flex items-center gap-1.5">
            {account ? (
              <>
                <ExchangeMark provider={account.provider} size={16} />
                {account.name}
              </>
            ) : (
              "No exchange linked"
            )}
          </span>
        </td>
      </tr>
      {rows.map((bt) => {
        const pnl = real[realPnlKey(bt.id, account?.id)];
        return (
          <tr key={bt.id} className="hover:bg-[var(--bg-hover)]">
            <td className={cell}>
              <Link to={`/live/${bt.id}`} className="font-semibold text-[var(--text-primary)] no-underline">
                {bt.name}
              </Link>
            </td>
            <td className={cn(cell, "num text-right")} style={{ color: pnl?.trades ? pnlColor(pnl.pnl) : undefined }} title="Realized from orders filled on the exchange">
              {pnl ? (pnl.trades ? formatMoney(pnl.pnl) : <span className="text-[var(--text-muted)]">No trades</span>) : <span className="text-[var(--text-muted)]">—</span>}
              {pnl?.trades ? <div className="text-[11px] font-normal text-[var(--text-muted)]">real, on exchange</div> : null}
            </td>
            <td className={cn(cell, "num text-right")} title="Share of closed trades that made money">
              {pnl?.trades ? (
                <>
                  {Math.round(pnl.winRate * 100)}%
                  <div className="text-[11px] font-normal text-[var(--text-muted)]">{Math.round(pnl.winRate * pnl.trades)}/{pnl.trades} won</div>
                </>
              ) : (
                <span className="text-[var(--text-muted)]">—</span>
              )}
            </td>
            <td className={cn(cell, "w-px text-right")}>{statusPill(bt.status, bt.enabled)}</td>
          </tr>
        );
      })}
    </>
  );
}
